/**
 * Hook for accessing invisible AI insights
 * Handles fetching, displaying, and dismissing insights
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import { useAuth } from '../../auth/context/AuthContext';
import logger from '@/shared/utils/core/logger';

/**
 * Hook to access background-generated insights for journal entries
 */
export function useInvisibleInsights() {
  const { user, loading: authLoading } = useAuth();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe to user's insights
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setLoading(false);
      setInsights([]);
      return;
    }

    setLoading(true);

    // Limit to 30 insights to prevent memory issues with large datasets
    const q = query(
      collection(db, COLLECTIONS.INSIGHTS),
      where('uid', '==', user.uid),
      where('dismissed', '==', false),
      orderBy('generatedAt', 'desc'),
      limit(30)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const insightsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Deduplicate as a safety net against Cloud Function retries creating
        // multiple docs for the same entry or signal combination.
        // Strategy: for background insights keyed by entryId, and signal insights
        // keyed by basedOnSignalsKey, keep only the newest among duplicates.
        const seen = new Map();
        const unique = [];
        for (const insight of insightsData) {
          let dedupeKey;
          if (insight.source === 'background' && insight.entryId) {
            dedupeKey = `bg:${insight.entryId}`;
          } else if (insight.basedOnSignalsKey) {
            dedupeKey = `sig:${insight.basedOnSignalsKey}`;
          }
          if (dedupeKey) {
            const existing = seen.get(dedupeKey);
            const thisTime = insight.generatedAt?.toMillis?.() || 0;
            const existTime = existing?.generatedAt?.toMillis?.() || 0;
            if (!existing || thisTime > existTime) {
              seen.set(dedupeKey, insight);
            }
          } else {
            unique.push(insight);
          }
        }
        unique.push(...seen.values());
        unique.sort((a, b) => {
          const aT = a.generatedAt?.toMillis?.() || 0;
          const bT = b.generatedAt?.toMillis?.() || 0;
          return bT - aT;
        });

        setInsights(unique);
        setLoading(false);
        setError(null);
      },
      (firestoreError) => {
        logger.error('Insights subscription error:', firestoreError);
        setLoading(false);
        setError('Failed to load insights');
      }
    );

    return () => unsubscribe();
  }, [user?.uid, authLoading]);

  /**
   * Mark an insight as displayed
   */
  const markDisplayed = useCallback(async (insightId) => {
    try {
      const insightRef = doc(db, COLLECTIONS.INSIGHTS, insightId);
      await updateDoc(insightRef, {
        displayedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.error('Failed to mark insight as displayed:', error);
    }
  }, []);

  /**
   * Dismiss an insight (permanently remove)
   */
  const dismissInsight = useCallback(async (insightId) => {
    try {
      const insightRef = doc(db, COLLECTIONS.INSIGHTS, insightId);
      await updateDoc(insightRef, {
        dismissed: true,
        dismissedAt: serverTimestamp(),
      });
    } catch (error) {
      logger.error('Failed to dismiss insight:', error);
      throw error;
    }
  }, []);

  /**
   * Archive an insight (hide from main view, but keep accessible)
   */
  const archiveInsight = useCallback(async (insightId, archive = true) => {
    try {
      const insightRef = doc(db, COLLECTIONS.INSIGHTS, insightId);
      await updateDoc(insightRef, {
        archived: archive,
        archivedAt: archive ? serverTimestamp() : null,
      });
    } catch (error) {
      logger.error('Failed to archive insight:', error);
      throw error;
    }
  }, []);

  /**
   * Mark an insight as read (removes from badge count)
   */
  const markAsRead = useCallback(async (insightId) => {
    try {
      const insightRef = doc(db, COLLECTIONS.INSIGHTS, insightId);
      await updateDoc(insightRef, {
        read: true,
        readAt: serverTimestamp(),
      });
    } catch (error) {
      logger.error('Failed to mark insight as read:', error);
      throw error;
    }
  }, []);

  /**
   * Get insights for a specific entry
   */
  const getInsightsForEntry = useCallback(
    (entryId) => {
      return insights.filter((insight) => insight.entryId === entryId);
    },
    [insights]
  );

  /**
   * Get the next undisplayed insight
   */
  const getNextInsight = useCallback(() => {
    return insights.find((insight) => !insight.displayedAt);
  }, [insights]);

  /**
   * Get insights that haven't been displayed yet
   */
  const getUndisplayedInsights = useCallback(() => {
    return insights.filter((insight) => !insight.displayedAt);
  }, [insights]);

  return {
    insights,
    loading,
    error,
    markDisplayed,
    dismissInsight,
    archiveInsight,
    markAsRead,
    getInsightsForEntry,
    getNextInsight,
    getUndisplayedInsights,
    hasUndisplayed: insights.some((i) => !i.displayedAt),
  };
}

/**
 * Hook specifically for strategic insight display
 * Returns ONE insight at a time based on priority
 */
export function useStrategicInsight() {
  const {
    getNextInsight,
    markDisplayed,
    dismissInsight,
    hasUndisplayed,
  } = useInvisibleInsights();

  const [currentInsight, setCurrentInsight] = useState(null);

  useEffect(() => {
    const nextInsight = getNextInsight();
    setCurrentInsight(nextInsight || null);
  }, [getNextInsight]);

  /**
   * Show the current insight and mark as displayed
   */
  const showInsight = useCallback(() => {
    if (currentInsight) {
      markDisplayed(currentInsight.id);
    }
  }, [currentInsight, markDisplayed]);

  /**
   * Hide from floating view only (don't permanently dismiss)
   * Insight will remain accessible in notification center
   */
  const hideFloating = useCallback(async () => {
    if (currentInsight) {
      // Just mark as displayed, don't dismiss
      // This makes it disappear from floating but stay in notification center
      await markDisplayed(currentInsight.id);
      setCurrentInsight(null);
    }
  }, [currentInsight, markDisplayed]);

  /**
   * Permanently dismiss (remove from notification center too)
   */
  const dismiss = useCallback(async () => {
    if (currentInsight) {
      await dismissInsight(currentInsight.id);
      setCurrentInsight(null);
    }
  }, [currentInsight, dismissInsight]);

  return {
    insight: currentInsight,
    showInsight,
    hideFloating,
    dismiss,
    hasMore: hasUndisplayed,
  };
}
